import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FaSearch, FaClock, FaUserGraduate, FaCertificate, FaArrowRight } from 'react-icons/fa';

// Sample course data
const courses = [
  {
    id: 1,
    title: "Strategic Leadership",
    description: "Master the art of strategic decision-making and team leadership in complex business environments.",
    duration: "8 Weeks",
    students: "1.2k+",
    level: "Advanced",
    image: "https://source.unsplash.com/random/600x400/?leadership",
    category: "Leadership"
  },
  {
    id: 2,
    title: "Digital Transformation",
    description: "Learn how to lead digital initiatives and transform traditional business models.",
    duration: "6 Weeks",
    students: "2.5k+",
    level: "Intermediate",
    image: "https://source.unsplash.com/random/600x400/?technology",
    category: "Technology"
  },
  {
    id: 3,
    title: "Financial Management",
    description: "Gain expertise in corporate finance, investment strategies, and financial analysis.",
    duration: "10 Weeks",
    students: "950+",
    level: "Intermediate",
    image: "https://source.unsplash.com/random/600x400/?finance",
    category: "Finance"
  },
  {
    id: 4,
    title: "Marketing Strategy",
    description: "Develop cutting-edge marketing strategies for the digital age.",
    duration: "5 Weeks",
    students: "3.1k+",
    level: "Beginner",
    image: "https://source.unsplash.com/random/600x400/?marketing",
    category: "Marketing"
  },
  {
    id: 5,
    title: "Operations Management",
    description: "Optimize business operations and supply chain management for maximum efficiency.",
    duration: "7 Weeks",
    students: "1.8k+",
    level: "Intermediate",
    image: "https://source.unsplash.com/random/600x400/?operations",
    category: "Operations"
  },
  {
    id: 6,
    title: "Human Resource Leadership",
    description: "Modern HR strategies for talent acquisition, retention, and organizational development.",
    duration: "6 Weeks",
    students: "1.5k+",
    level: "Beginner",
    image: "https://source.unsplash.com/random/600x400/?hr",
    category: "HR"
  }
];

const categories = ["All", "Leadership", "Technology", "Finance", "Marketing", "Operations", "HR"];

const ManagementContainer = styled.div`
  max-width: 1400px;
  margin: 0 auto;
  padding: 4rem 2rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 3rem;
`;

const Title = styled(motion.h2)`
  font-size: 3rem;
  font-weight: 800;
  color: #1a1a1a;
  margin-bottom: 1rem;
  background: linear-gradient(90deg, #3b82f6, #8b5cf6);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
`;

const Subtitle = styled.p`
  font-size: 1.2rem;
  color: #4b5563;
  max-width: 700px;
  margin: 0 auto;
  line-height: 1.6;
`;

const SearchFilterContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 3rem;
  flex-wrap: wrap;
  gap: 1rem;
`;

const SearchBar = styled.div`
  position: relative;
  flex-grow: 1;
  max-width: 500px;

  input {
    width: 100%;
    padding: 0.8rem 1rem 0.8rem 2.5rem;
    font-size: 1rem;
    border: 1px solid #e5e7eb;
    border-radius: 8px;
    transition: all 0.3s ease;
    
    &:focus {
      outline: none;
      border-color: #8b5cf6;
      box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.2);
    }
  }
`;

const SearchIcon = styled.div`
  position: absolute;
  left: 1rem;
  top: 50%;
  transform: translateY(-50%);
  color: #6b7280;
`;

const FilterButtons = styled.div`
  display: flex;
  gap: 0.8rem;
  flex-wrap: wrap;
`;

const FilterButton = styled(motion.button)`
  padding: 0.6rem 1.2rem;
  font-size: 0.9rem;
  font-weight: 600;
  border: none;
  border-radius: 50px;
  background: ${({ active }) => active ? '#8b5cf6' : '#f3f4f6'};
  color: ${({ active }) => active ? 'white' : '#4b5563'};
  cursor: pointer;
  transition: all 0.3s ease;
  
  &:hover {
    background: ${({ active }) => active ? '#7c3aed' : '#e5e7eb'};
  }
`;

const CoursesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  gap: 2rem;
`;

const CourseCard = styled(motion.div)`
  background: white;
  border-radius: 16px;
  overflow: hidden;
  box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const CourseImage = styled(motion.div)`
  height: 200px;
  overflow: hidden;
  position: relative;
  
  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: transform 0.5s ease;
  }
`;

const CourseBadge = styled.span`
  position: absolute;
  top: 1rem;
  right: 1rem;
  background: rgba(0,0,0,0.7);
  color: white;
  padding: 0.3rem 0.8rem;
  border-radius: 50px;
  font-size: 0.8rem;
  font-weight: 600;
`;

const CourseContent = styled.div`
  padding: 1.5rem;
  flex-grow: 1;
  display: flex;
  flex-direction: column;
`;

const CourseTitle = styled.h3`
  font-size: 1.5rem;
  font-weight: 700;
  color: #1a1a1a;
  margin-bottom: 0.75rem;
`;

const CourseDescription = styled.p`
  font-size: 0.95rem;
  color: #4b5563;
  line-height: 1.6;
  margin-bottom: 1.5rem;
  flex-grow: 1;
`;

const CourseMeta = styled.div`
  display: flex;
  gap: 1rem;
  margin-bottom: 1.5rem;
  flex-wrap: wrap;
`;

const MetaItem = styled.div`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #6b7280;
`;

const EnrollButton = styled(motion.button)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: linear-gradient(135deg, #8b5cf6, #3b82f6);
  color: white;
  border: none;
  padding: 0.8rem 1.5rem;
  font-size: 1rem;
  font-weight: 600;
  border-radius: 8px;
  cursor: pointer;
  margin-top: auto;
  transition: all 0.3s ease;
  
  svg {
    transition: transform 0.3s ease;
  }
  
  &:hover {
    box-shadow: 0 5px 15px rgba(139, 92, 246, 0.4);
    
    svg {
      transform: translateX(3px);
    }
  }
`;

const Management = () => {
  const [selectedCategory, setSelectedCategory] = useState("All");
  const [searchQuery, setSearchQuery] = useState("");

  const filteredCourses = courses.filter(course => {
    const matchesCategory = selectedCategory === "All" || course.category === selectedCategory;
    const matchesSearch = course.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
                         course.description.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesSearch;
  });

  return (
    <ManagementContainer>
      <Header>
        <Title
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          Management Courses
        </Title>
        <Subtitle>
          Elevate your leadership skills with our comprehensive management programs designed for professionals at all levels.
        </Subtitle>
      </Header>

      <SearchFilterContainer>
        <SearchBar>
          <SearchIcon>
            <FaSearch />
          </SearchIcon>
          <input 
            type="text" 
            placeholder="Search courses..." 
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </SearchBar>
        
        <FilterButtons>
          {categories.map(category => (
            <FilterButton
              key={category}
              active={selectedCategory === category}
              onClick={() => setSelectedCategory(category)}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
            >
              {category}
            </FilterButton>
          ))}
        </FilterButtons>
      </SearchFilterContainer>

      <CoursesGrid>
        <AnimatePresence>
          {filteredCourses.map(course => (
            <CourseCard
              key={course.id}
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.9 }}
              transition={{ duration: 0.4 }}
              whileHover={{ y: -5 }}
            >
              <CourseImage>
                <motion.img
                  src={course.image}
                  alt={course.title}
                  whileHover={{ scale: 1.05 }}
                  transition={{ duration: 0.5 }}
                />
                <CourseBadge>{course.level}</CourseBadge>
              </CourseImage>
              <CourseContent>
                <CourseTitle>{course.title}</CourseTitle>
                <CourseDescription>{course.description}</CourseDescription>
                
                <CourseMeta>
                  <MetaItem>
                    <FaClock /> {course.duration}
                  </MetaItem>
                  <MetaItem>
                    <FaUserGraduate /> {course.students}
                  </MetaItem>
                  <MetaItem>
                    <FaCertificate /> {course.category}
                  </MetaItem>
                </CourseMeta>
                
                <EnrollButton
                  whileHover={{ scale: 1.02 }}
                  whileTap={{ scale: 0.98 }}
                >
                  Enroll Now <FaArrowRight />
                </EnrollButton>
              </CourseContent>
            </CourseCard>
          ))}
        </AnimatePresence>
      </CoursesGrid>
    </ManagementContainer>
  );
};

export default Management;